import {Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output, signal, ChangeDetectionStrategy} from '@angular/core';

import {Observable, Subscription} from 'rxjs';
import {distinctUntilChanged, filter, map} from 'rxjs/operators';
import {Store} from '@ngrx/store';
import {Tile, TileId, TILE_TYPE_SIMPLE_ANALOG} from '@shared/models/tile';
import {AppState} from 'src/app/app.state';
import {TilesService} from '@core/services/tiles.service';
import {TilePollingService, PollConsumerId} from '@core/services/tile-polling.service';
import {AlarmsService} from '@core/services/alarms.service';
import {ObjectType} from '@shared/models/object.model';
import {EMPTY_ID} from '@shared/constants/empty-id';
import {AlarmBadgeStage, aggregateAlarmBadgeStage, TILE_COLOR_CSS} from '@features/canvas-tiles/shared/canvas-tile.constants';
import {FaIconComponent} from '@lib/components/fa-icon/fa-icon.component';
import {TileValueParser} from '@shared/models/tile-value-parser';
import {SimpleAnalogStructure} from '@shared/models/structure';
import {SpinnerComponent} from '@lib/components/spinner/spinner.component';
import {isValidTileColor} from '@shared/utils/color.util';
import {ReplayTileResult} from '@core/services/canvas-replay.service';
import {calculateDataAgeFromTimestamp} from '@shared/utils/data-age.util';
import {MAX_DECIMAL_PLACES, DEFAULT_STALE_THRESHOLD_MINUTES, DATA_AGE_REFRESH_INTERVAL_MS} from '@shared/constants/tile-config.constants';

interface TileDisplayData {
  tileId: TileId;
  tile: Tile | null;
  displayValue: string;
  displayIcon: string;
  displayColor: string;
  displayUnits: string;
  displayAge: string;
  isStale: boolean;
  loading: boolean;
  parser: TileValueParser | null;
  rawValue: string | number | boolean | null;
  alarmBadge: AlarmBadgeStage;
}

/**
 * A compact tile component designed for display on canvas overlays.
 * Shows minimal tile information: icon and current value.
 * Can display either a single tile or multiple tiles stacked together.
 * Used in Canvas tiles to represent placed tiles on images.
 */
@Component({
  selector: 'motn-canvas-mini-tile',
  standalone: true,
  imports: [FaIconComponent, SpinnerComponent],
  templateUrl: './canvas-mini-tile.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./canvas-mini-tile.component.scss']
})
export class CanvasMiniTileComponent implements OnInit, OnDestroy {
  @Input() tileIds: TileId[] = [];
  @Input() hideIcon = false;
  @HostBinding('class.compact') @Input() compact = false;

  /** Direct-value mode: renders without any tile ID or store subscription. */
  @Input() set directValue(v: number | boolean | null | undefined) {
    this._directValue = v;
    this.syncDirectEntry();
  }
  @Input() set directValueTime(t: string) {
    this._directValueTime = t;
    this.syncDirectEntry();
  }
  @Input() set directValueOffset(v: number) {
    this._directValueOffset = v;
    this.syncDirectEntry();
  }
  @Input() staleThresholdMinutes = DEFAULT_STALE_THRESHOLD_MINUTES;

  @Input() set shouldPoll(value: boolean) {
    this._shouldPoll = value;
    this.handlePollingStateChange();
  }
  get shouldPoll(): boolean {
    return this._shouldPoll;
  }
  /**
   * Replay mode: fetch historical values at this timestamp instead of polling live data
   */
  @Input() set replayTimestamp(value: number | null | undefined) {
    this._replayTimestamp = value;
    if (value) {
      this.tilePollingService.stopAllPolling(this.pollConsumerId);
      this.setReplayLoading();
    } else if (this.shouldPoll) {
      this.handlePollingStateChange();
    }
  }
  get replayTimestamp(): number | null | undefined {
    return this._replayTimestamp;
  }
  @Output() clicked = new EventEmitter<{event: MouseEvent, tileId?: TileId, index?: number, tiles?: Tile[]}>();
  colors = signal<string[]>([]);
  alarmBadge = signal<AlarmBadgeStage>(null);

  tilesData: TileDisplayData[] = [];
  private _replayTimestamp: number | null | undefined;

  @Input() set replayResult$(obs: Observable<ReplayTileResult>) {
    this.replayResultSub?.unsubscribe();
    this.replayResultSub = obs.pipe(
      filter(r => this.tilesData.some(td => td.tileId === r.tileId))
    ).subscribe(result => this.applyReplayResult(result));
  }

  private _directValue: number | boolean | null | undefined;
  private _directValueTime = '';
  private _directValueOffset = 0;
  private _shouldPoll = false;
  private tileSubs: Map<TileId, Subscription> = new Map();
  private valueSubs: Map<TileId, Subscription> = new Map();
  private replayResultSub?: Subscription;
  private alarmSub?: Subscription;
  private ageRefreshTimer: ReturnType<typeof setInterval> | null = null;
  /** Stable ID that uniquely identifies this component instance in the polling service. */
  private readonly pollConsumerId: PollConsumerId = crypto.randomUUID();

  constructor(
    private tilesService: TilesService,
    private tilePollingService: TilePollingService,
    private alarmsService: AlarmsService,
    private store: Store<AppState>,
  ) {}

  ngOnInit(): void {
    if (this._directValue !== undefined) {
      this.ageRefreshTimer = setInterval(() => {
        if (this.tilesData[0]) {
          const r = calculateDataAgeFromTimestamp(this._directValueTime, { minAgeMinutes: this.staleThresholdMinutes });
          this.tilesData[0].displayAge = r.dataAge;
          this.tilesData[0].isStale = r.isStale;
        }
      }, DATA_AGE_REFRESH_INTERVAL_MS);
      return;
    }

    if (this.tileIds.length === 0 || this.tileIds[0] === EMPTY_ID) {
      return;
    }

    // Refresh age labels every 5s (same cadence as tile-base)
    this.ageRefreshTimer = setInterval(() => {
      this.tilesData.forEach(td => {
        const tv = this.tilesService.tileValues[td.tileId];
        if (tv?.Time) {
          const r = calculateDataAgeFromTimestamp(tv.Time);
          td.displayAge = r.dataAge;
          td.isStale = r.isStale;
        }
      });
    }, DATA_AGE_REFRESH_INTERVAL_MS);

    this.tilesData = this.tileIds.map(tileId => ({
      tileId,
      tile: null,
      displayValue: '',
      displayIcon: '',
      displayColor: '',
      displayUnits: '',
      displayAge: '',
      isStale: false,
      loading: true,
      parser: null,
      rawValue: null,
      alarmBadge: null,
    }));
    // Seed colors() with the right length before any tile data resolves.
    this.emitTileColors();

    this.tileIds.forEach((tileId, index) => {
      this.subscribeToTile(tileId, index);
    });

    this.subscribeToAlarms();
  }

  ngOnDestroy(): void {
    this.tileSubs.forEach(sub => sub.unsubscribe());
    this.valueSubs.forEach(sub => sub.unsubscribe());
    this.replayResultSub?.unsubscribe();
    this.alarmSub?.unsubscribe();
    this.tilePollingService.stopAllPolling(this.pollConsumerId);
    if (this.ageRefreshTimer !== null) {
      clearInterval(this.ageRefreshTimer);
    }
  }

  private subscribeToAlarms(): void {
    this.alarmSub = this.alarmsService.activeEvents$.subscribe(events => {
      this.tilesData.forEach(tileData => {
        const event = events.find(e => e.ObjectID === tileData.tileId && e.ObjectType === ObjectType.TILE);
        tileData.alarmBadge = event ? (event.AckTime ? 'acked' : 'unacked') : null;
      });
      this.updateAlarmBadgeSignal();
    });
  }

  private updateAlarmBadgeSignal(): void {
    const stage = this._replayTimestamp ? null : aggregateAlarmBadgeStage(this.tilesData.map(td => td.alarmBadge));
    this.alarmBadge.set(stage);
  }

  private subscribeToTile(tileId: TileId, index: number): void {
    const tileSub = this.store.select((state: AppState) => state.tiles.all[tileId]).subscribe(tile => {
      if (!tile) {
        this.tilePollingService.stopPolling(this.pollConsumerId, tileId);
        return;
      }
      this.tilesData[index].tile = Tile.create(tile);
      this.updateDisplay(index);
    });
    this.tileSubs.set(tileId, tileSub);

    const existingTile = this.store.selectSignal((state: AppState) => state.tiles.all[tileId])();
    if (!existingTile) {
      // Fetch tile if not in store. Store the sub so it is cancelled on destroy.
      const fetchSub = this.tilesService.fetchTileBatched$(tileId).subscribe({
        error: () => {
          this.tilesData[index].loading = false;
        }
      });
      this.tileSubs.set(`fetch:${tileId}`, fetchSub);
    } else {
      this.tilesData[index].tile = Tile.create(existingTile);
      this.updateDisplay(index);
    }
  }

  private updateDisplay(index: number): void {
    const tileData = this.tilesData[index];
    if (!tileData.tile) return;

    if (tileData.tile.Type === TILE_TYPE_SIMPLE_ANALOG) {
      const structure = tileData.tile.Structure as SimpleAnalogStructure;
      tileData.displayUnits = structure.DisplayRawUnits || '';
    } else {
      tileData.displayUnits = '';
    }

    if (this.shouldPoll && !this._replayTimestamp) {
      this.tilePollingService.startPolling(this.pollConsumerId, tileData.tile);
    }

    const existingSub = this.valueSubs.get(tileData.tileId);
    existingSub?.unsubscribe();

    const valueSub = this.tilesService.tileValues$.pipe(
      map(tv => tv[tileData.tileId]),
      distinctUntilChanged(),
    ).subscribe(value => {
      if (!value || this._replayTimestamp) {
        return;
      }

      tileData.rawValue = value.Value;

      if (!tileData.parser) {
        tileData.parser = new TileValueParser(tileData.tile!, value.Value);
      } else {
        tileData.parser.tile = tileData.tile!;
        tileData.parser.rawValue = value.Value;
      }

      tileData.displayValue = tileData.parser.getDisplayValue();
      const ageResult = calculateDataAgeFromTimestamp(value.Time);
      tileData.displayAge = ageResult.dataAge;
      tileData.isStale = ageResult.isStale;
      this.setColorAndIcon(index);
      tileData.loading = false;
    });
    this.valueSubs.set(tileData.tileId, valueSub);
  }

  /**
   * Handles changes to the shouldPoll state.
   * When shouldPoll becomes true, starts polling for all tiles.
   * When shouldPoll becomes false, stops polling for all tiles.
   */
  private handlePollingStateChange(): void {
    if (this.shouldPoll && !this._replayTimestamp) {
      this.tilesData
        .filter(tileData => tileData.tile)
        .forEach(tileData => {
          this.tilePollingService.startPolling(this.pollConsumerId, tileData.tile!);
        });
    } else {
      this.tilePollingService.stopAllPolling(this.pollConsumerId);
    }
  }

  onTileClick(event: MouseEvent, tileId: TileId, index: number): void {
    event.preventDefault();
    event.stopPropagation();
    const tiles = this.tilesData.map(td => td.tile).filter((t): t is Tile => t !== null);
    this.clicked.emit({event, tileId, index, tiles});
  }


  /**
   * Sets the color and icon based on alarm conditions.
   * Checks if the current tile value violates any alarm condition and sets color accordingly.
   */
  private setColorAndIcon(index: number): void {
    const tileData = this.tilesData[index];
    if (!tileData.tile) return;

    // Check if alarm condition is met (only call getFulfilledCondition once)
    const condition = tileData.tile.getFulfilledCondition(tileData.rawValue);
    const alarmColor = tileData.tile.getAlarmColor(condition);

    if (alarmColor && condition) {
      tileData.displayColor = alarmColor;
      tileData.displayIcon = condition.Icon
        ? `fa-regular fa-${condition.Icon}`
        : (tileData.tile.Appearance.Icon ? `fa-regular fa-${tileData.tile.Appearance.Icon}` : '');
    } else {
      tileData.displayColor = isValidTileColor(tileData.tile.Appearance.Color || '');
      tileData.displayIcon = tileData.tile.Appearance.Icon ? `fa-regular fa-${tileData.tile.Appearance.Icon}` : '';
    }

    this.emitTileColors();
  }

  private emitTileColors(): void {
    this.colors.set(this.tilesData.map(td => TILE_COLOR_CSS[td.displayColor] ?? TILE_COLOR_CSS['']));
  }

  private setReplayLoading(): void {
    this.tilesData.forEach(td => td.loading = true);
  }

  private applyReplayResult(result: ReplayTileResult): void {
    const tileData = this.tilesData.find(td => td.tileId === result.tileId);
    if (!tileData) return;

    if (result.hasData && result.value !== null) {
      this.updateTileWithHistoricalValue(tileData, result.value);
    } else {
      this.updateTileWithNoData(tileData);
    }

    tileData.loading = false;
  }

  private updateTileWithHistoricalValue(tileData: TileDisplayData, value: string | number | boolean): void {
    tileData.rawValue = value;

    if (!tileData.parser) {
      tileData.parser = new TileValueParser(tileData.tile!, value);
    } else {
      tileData.parser.rawValue = value;
    }

    tileData.displayValue = tileData.parser.getDisplayValue();
    this.setColorAndIcon(this.tilesData.indexOf(tileData));
  }

  private updateTileWithNoData(tileData: TileDisplayData): void {
    tileData.displayValue = '';
    tileData.rawValue = null;
    tileData.displayColor = isValidTileColor(tileData.tile?.Appearance.Color || '');
    tileData.displayIcon = tileData.tile?.Appearance.Icon ? `fa-regular fa-${tileData.tile.Appearance.Icon}` : '';
    this.emitTileColors();
  }

  private formatDirectValue(value: number | boolean): string {
    if (typeof value === 'boolean') return String(value);
    return String(+(value + this._directValueOffset).toFixed(MAX_DECIMAL_PLACES));
  }

  private syncDirectEntry(): void {
    if (this._directValue == null) return;

    const ageResult = calculateDataAgeFromTimestamp(this._directValueTime, { minAgeMinutes: this.staleThresholdMinutes });
    const entry: TileDisplayData = {
      tileId: 'direct' as TileId,
      tile: null,
      displayValue: this.formatDirectValue(this._directValue),
      displayIcon: '',
      displayColor: '',
      displayUnits: '',
      displayAge: ageResult.dataAge,
      isStale: ageResult.isStale,
      loading: false,
      parser: null,
      rawValue: this._directValue,
      alarmBadge: null,
    };

    if (this.tilesData.length && this.tilesData[0].tileId === ('direct' as TileId)) {
      this.tilesData[0] = entry;
    } else {
      this.tilesData = [entry];
    }
  }

}
